import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  ShieldCheck,
  Timer,
} from 'lucide-react';

import OtpStep from 'features/partner-auth/components/OtpStep';

import { useDashboardReveal, type RevealState } from '../hooks/useDashboardReveal';
import type { DashboardAccess } from '../types';

const PLACEHOLDER_DOTS = '••••••••';

const DashboardAccessCard: React.FC = () => {
  const { t } = useTranslation();
  const {
    state,
    errorMessage,
    access,
    challenge,
    revealSecondsLeft,
    sendOtp,
    resendOtp,
    verify,
    lockNow,
    cancel,
  } = useDashboardReveal();

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="rounded-3xl bg-white border border-gray-100 p-6 sm:p-8 shadow-sm"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-600 border border-amber-100">
          <KeyRound className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-gray-900">
            {t('partnerPortal.dashboardAccess.heading')}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('partnerPortal.dashboardAccess.subheading')}
          </p>
        </div>
        {state === 'revealed' && revealSecondsLeft > 0 && (
          <CountdownChip secondsLeft={revealSecondsLeft} />
        )}
      </div>

      {state === 'awaitingCode' || state === 'verifying' ? (
        challenge && (
          <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-5">
            <OtpStep
              sentTo={challenge.sentTo}
              expiresIn={challenge.otpExpiresIn}
              isVerifying={state === 'verifying'}
              isSuccess={false}
              errorMessage={
                errorMessage
                  ? `${t('partnerPortal.dashboardAccess.errors.verifyFailed')}${
                      errorMessage ? ` (${errorMessage})` : ''
                    }`
                  : null
              }
              devOtp={challenge.devOtp ?? null}
              backLabel={String(t('partnerPortal.dashboardAccess.otp.cancel'))}
              onBack={cancel}
              onSubmit={verify}
              onResend={resendOtp}
            />
          </div>
        )
      ) : state === 'revealed' && access ? (
        <RevealedRows access={access} onLockNow={lockNow} />
      ) : (
        <LockedView
          state={state}
          errorMessage={errorMessage}
          onSendOtp={sendOtp}
        />
      )}
    </motion.section>
  );
};

interface LockedViewProps {
  state: RevealState;
  errorMessage: string | null;
  onSendOtp: () => void;
}

const LockedView: React.FC<LockedViewProps> = ({
  state,
  errorMessage,
  onSendOtp,
}) => {
  const { t } = useTranslation();
  const isSending = state === 'sending';

  return (
    <div>
      <div className="divide-y divide-gray-100 rounded-2xl border border-gray-100 bg-gray-50/50">
        {(['userName', 'password', 'dashboardId'] as const).map((key) => (
          <div
            key={key}
            className="flex items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {t(`partnerPortal.dashboardAccess.${key}`)}
              </div>
              <div className="font-mono text-sm text-gray-400 mt-0.5 truncate select-none">
                {PLACEHOLDER_DOTS}
              </div>
            </div>
            <Lock className="h-4 w-4 text-gray-300 flex-shrink-0" />
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm text-amber-900">
          <div className="font-semibold">
            {t('partnerPortal.dashboardAccess.locked.title')}
          </div>
          <p className="text-amber-800/90 mt-0.5">
            {t('partnerPortal.dashboardAccess.locked.subtitle')}
          </p>
        </div>
      </div>

      {state === 'error' && errorMessage && (
        <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">
          {t('partnerPortal.dashboardAccess.errors.sendFailed')}
        </div>
      )}

      <button
        type="button"
        onClick={onSendOtp}
        disabled={isSending}
        className="mt-4 w-full rounded-2xl bg-haat-red-600 text-white font-semibold py-3 flex items-center justify-center gap-2 hover:bg-haat-red-700 disabled:opacity-60"
      >
        {isSending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ShieldCheck className="h-4 w-4" />
        )}
        {t('partnerPortal.dashboardAccess.locked.cta')}
      </button>
    </div>
  );
};

interface RevealedRowsProps {
  access: DashboardAccess;
  onLockNow: () => void;
}

const RevealedRows: React.FC<RevealedRowsProps> = ({ access, onLockNow }) => {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copy = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(
        () => setCopiedKey((k) => (k === key ? null : k)),
        1500,
      );
    } catch {
      // Clipboard unavailable (insecure context). Silently ignore.
    }
  };

  const rows: Array<{
    key: 'userName' | 'password' | 'dashboardId';
    value: string | undefined;
    secret?: boolean;
  }> = [
    { key: 'userName', value: access.userName },
    { key: 'password', value: access.password, secret: true },
    { key: 'dashboardId', value: access.dashboardId },
  ];

  const visibleRows = rows.filter((r) => !!r.value);
  if (visibleRows.length === 0) return null;

  return (
    <>
      <div className="divide-y divide-gray-100 rounded-2xl border border-gray-100 bg-gray-50/50">
        {visibleRows.map((row) => {
          const label = t(`partnerPortal.dashboardAccess.${row.key}`);
          const isCopied = copiedKey === row.key;
          const displayValue =
            row.secret && !revealed
              ? '•'.repeat(Math.max(row.value!.length, 6))
              : row.value!;
          return (
            <div
              key={row.key}
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {label}
                </div>
                <div className="font-mono text-sm text-gray-900 mt-0.5 truncate">
                  {displayValue}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {row.secret && (
                  <button
                    type="button"
                    onClick={() => setRevealed((v) => !v)}
                    aria-label={String(
                      revealed
                        ? t('partnerPortal.dashboardAccess.hide')
                        : t('partnerPortal.dashboardAccess.show'),
                    )}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    {revealed ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => copy(row.value!, row.key)}
                  aria-label={String(
                    isCopied
                      ? t('partnerPortal.dashboardAccess.copied')
                      : t('partnerPortal.dashboardAccess.copy'),
                  )}
                  className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-xl text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  {isCopied ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="text-emerald-700">
                        {t('partnerPortal.dashboardAccess.copied')}
                      </span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      <span>{t('partnerPortal.dashboardAccess.copy')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onLockNow}
        className="mt-4 w-full rounded-2xl border-2 border-gray-200 bg-white text-gray-700 font-semibold py-3 flex items-center justify-center gap-2 hover:bg-gray-50"
      >
        <Lock className="h-4 w-4" />
        {t('partnerPortal.dashboardAccess.revealed.lockNow')}
      </button>
    </>
  );
};

const CountdownChip: React.FC<{ secondsLeft: number }> = ({ secondsLeft }) => {
  const { t } = useTranslation();
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = (secondsLeft % 60).toString().padStart(2, '0');
  return (
    <div className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-xs font-semibold flex-shrink-0">
      <Timer className="h-3.5 w-3.5" />
      <span className="tabular-nums" dir="ltr">
        {t('partnerPortal.dashboardAccess.revealed.hidesIn', {
          time: `${minutes}:${seconds}`,
        })}
      </span>
    </div>
  );
};

export default DashboardAccessCard;
